import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';

interface PaymentSuccessModalProps {
  cartId: string;
  isOpen: boolean;
  onClose: () => void;
  paymentIntentId?: string;
  isGuest?: boolean;
  clubHandle?: string | null;
}

const PaymentSuccessModal: React.FC<PaymentSuccessModalProps> = ({
  cartId,
  isOpen,
  onClose,
  isGuest = false,
  clubHandle = null,
}) => {
  const navigate = useNavigate();
  // Note: Cart count is already updated by PaymentPage.handlePaymentSuccess

  const handleViewCart = () => {
    navigate(`/carts/${cartId}`);
    onClose();
  };

  const handleLogin = () => {
    navigate('/login', { state: { returnTo: `/carts/${cartId}` } });
    onClose();
  };

  const handleContinueShopping = () => {
    if (clubHandle) {
      navigate(`/venues/${clubHandle}`);
    } else {
      navigate('/venues');
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.3
            }}
            className="relative bg-gray-950 border border-gray-800/50 rounded-2xl p-8 max-w-md w-full shadow-2xl"
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.1
              }}
              className="flex justify-center mb-6"
            >
              <div className="w-24 h-24 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                <motion.svg
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                    delay: 0.2
                  }}
                  className="w-12 h-12 text-green-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </motion.svg>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl font-bold text-white mb-3">Payment Successful!</h2>
              {isGuest ? (
                <p className="text-gray-300 text-base leading-relaxed">
                  Your payment has been processed successfully. Your booking is now confirmed. Please log in to access tickets and download your invoice.
                </p>
              ) : (
                <p className="text-gray-300 text-base leading-relaxed">
                  Your payment has been processed successfully. Your booking is now confirmed and you can download your invoice.
                </p>
              )}
            </motion.div>

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col gap-3"
            >
              {isGuest ? (
                <>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleLogin}
                    className="w-full px-6 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors duration-200 shadow-lg shadow-blue-500/30"
                  >
                    Login / Signup
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleContinueShopping}
                    className="w-full px-6 py-3.5 bg-gray-800/80 hover:bg-gray-800 text-gray-200 rounded-xl font-medium transition-colors duration-200 border border-gray-700/50"
                  >
                    Continue Shopping
                  </motion.button>
                </>
              ) : (
                <>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleViewCart}
                    className="w-full px-6 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors duration-200 shadow-lg shadow-green-500/30"
                  >
                    View Cart & Download Invoice
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className="w-full px-6 py-3.5 bg-gray-800/80 hover:bg-gray-800 text-gray-200 rounded-xl font-medium transition-colors duration-200 border border-gray-700/50"
                  >
                    Close
                  </motion.button>
                </>
              )}
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentSuccessModal;

